// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Organization IDs that have access to specific features.
 * This allows us to gate features to specific organizations during development.
 */
const FEATURE_ORGANIZATION_IDS = {
  // Organization IDs that have access to project workflows feature
  PROJECT_WORKFLOWS: [
    '423f1284-ef4b-4b75-a3f4-e428b90f1f78', // Smart device
  ] as string[],
  // Organization IDs that have access to bill of materials feature
  BOM: [
    '423f1284-ef4b-4b75-a3f4-e428b90f1f78', // Smart device
  ] as string[],
};

/**
 * Feature flag configuration interface
 */
export interface FeatureFlags {
  enableProjectWorkflows: boolean;
  enableBOM: boolean;
}

const SYSTEM_USER_GROUP = 'SYSTEM_USER';

/**
 * Returns feature flags with all features enabled.
 * Used for system users who bypass feature gating.
 */
function getAllFlagsEnabled(): FeatureFlags {
  return {
    enableProjectWorkflows: true,
    enableBOM: true,
  };
}

/**
 * Determines which features are enabled for a given organization.
 *
 * @param organizationId - The ID of the organization to check features for
 * @returns An object containing all feature flags and their enabled state
 */
function getFeatureFlags(organizationId: string): FeatureFlags {
  return {
    enableProjectWorkflows:
      FEATURE_ORGANIZATION_IDS.PROJECT_WORKFLOWS.includes(organizationId),
    enableBOM: FEATURE_ORGANIZATION_IDS.BOM.includes(organizationId),
  };
}

/**
 * Determines which features are enabled for a user based on their organization
 * and user groups. System users bypass all feature flags.
 *
 * @param organizationId - The ID of the organization to check features for
 * @param userGroups - The groups the user belongs to
 * @returns An object containing all feature flags and their enabled state
 */
export function getFeatureFlagsForUser(
  organizationId: string,
  userGroups: string[]
): FeatureFlags {
  if (userGroups.includes(SYSTEM_USER_GROUP)) {
    return getAllFlagsEnabled();
  }
  return getFeatureFlags(organizationId);
}
